import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import Layout from "../Layout/Layout";
import "../css/journal.css";
import JournalTable from "../Components/JournalTable";
import axios from "axios";
import { API_BASE } from "../Utils/constant";
import { useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";

import ShareDocumentModal from "../Components/ShareDocumentModal";
import { AUTH_TOKEN } from "../constants/localStorageKeys";
import { retrieve } from "../helpers/StorageHelper";
import DoneAchievedCard from "../Components/TaskManagement/DoneAchievedCard";

/** Build modal rows from nested reflection questions (matches single-detail API shape). */
function buildReflectionAnswersFromUserReflection(userReflection) {
  const reflectionId = userReflection?.id;
  const mainQuestions = userReflection?.reflection?.main_questions ?? [];
  return [
    ...mainQuestions.map((question) => ({
      main_question: {
        question: question.question,
        keywords: question.keywords,
      },
      text: question.answer?.text || "",
      user_reflection_id: reflectionId,
    })),
    ...mainQuestions.flatMap((question) =>
      (question.follow_up_questions ?? []).map((followUp) => ({
        follow_up_question: {
          question: followUp.question,
          keywords: followUp.keywords,
        },
        text: followUp.answer?.text || "",
        user_reflection_id: reflectionId,
      })),
    ),
  ];
}

function transformUserReflectionForModal(userReflection) {
  if (!userReflection) return userReflection;
  const mainQuestions = userReflection?.reflection?.main_questions ?? [];
  const built = buildReflectionAnswersFromUserReflection(userReflection);
  if (mainQuestions.length > 0) {
    return { ...userReflection, reflection_answers: built };
  }
  return {
    ...userReflection,
    reflection_answers: userReflection.reflection_answers ?? [],
  };
}

export default function ReflectionLibrary() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reflectionTypes, setReflectionTypes] = useState([]);

  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const [reflectionDetails, setReflectionDetails] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);

  const [reflectionToDelete, setReflectionToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDownloadSelected, setIsDownloadSelected] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileToShare, setFileToShare] = useState(null);
  const [titleToShare, setTitleToShare] = useState("Share your Reflection");
  const [contentToShare, setContentToShare] = useState("");

  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1,
  });

  const [filters, setFilters] = useState({
    reflection_type: "",
    main_question: "",
    sort_by: "created_at",
    sort_order: "desc",
  });

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedReflection, setSelectedReflection] = useState(null);
  const modalRef = useRef(null);

  const searchTimeoutRef = useRef(null);

  const token = retrieve(AUTH_TOKEN);

  const toggleModal = (reflection = null) => {
    setSelectedReflection(reflection);
    setIsModalVisible(!isModalVisible);
  };

  // settings modal
  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsModalVisible(false);
      }
    }

    if (isModalVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isModalVisible]);

  const handleDownloadPDF = () => {
    if (!modalRef1.current) return;

    const opt = {
      margin: [0.1, 0.1, 0.1, 0.1],
      filename: "journal-summary.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
      },
      jsPDF: {
        unit: "in",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf()
      .set(opt)
      .from(modalRef1.current)
      .save()
      .then(() => {});
  };

  const handleGenerateAndOpenModal = async () => {
    if (!modalRef1.current) return;
    const clonedContent = modalRef1.current.cloneNode(true);

    const elementToRemove = clonedContent.querySelectorAll(".close-button");
    elementToRemove.forEach((el) => el.remove());

    const buttonToRemove = clonedContent.querySelector(".btn-2.btn-1-primary");
    if (buttonToRemove) {
      buttonToRemove.remove();
    }

    const style = document.createElement("style");
    style.innerHTML = `
      h5.text-white, h2, .text-body-b3{
        color: #000 !important;
      }

      .col-md-6{
        width: 100% !important;
      }

      .page-break {
        page-break-before: always; /* Forces a new page */
      }

      .no-break {
        page-break-inside: avoid; /* Prevents cutting inside this element */
      }

      .habit-sub-card{
        page-break-inside: avoid;
      }
    `;
    clonedContent.prepend(style);

    const options = {
      margin: 0.5,
      filename: "journal-report.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    try {
      const pdfBlob = await html2pdf()
        .from(clonedContent)
        .set(options)
        .outputPdf("blob");

      // Create a File object from the Blob
      const pdfFile = new File([pdfBlob], "journal-report.pdf", {
        type: "application/pdf",
      });
      setFileToShare(pdfFile);
      setShowReflectionModal(false);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFileToShare(null); // Clear the file after closing the modal
  };

  // fetch all reflections
  const fetchReflections = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API_BASE}/reflections-library`, {
        params: {
          page: pagination.current_page,
          per_page: pagination.per_page,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setReflections(response.data.data.user_reflections);
      setPagination(response.data.data.meta);
      setError(null);
    } catch (err) {
      console.error("Error fetching reflections:", err);
      setError(err.response?.data?.message || "Failed to fetch reflections");
      setReflections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReflections();
  }, [filters, pagination.current_page, pagination.per_page]);

  // store reflection types
  useEffect(() => {
    // You might want to fetch reflection types from an API or define them locally
    const types = ["Am", "Pm"];
    setReflectionTypes(types);
  }, []);

  // filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  // pagination
  const handlePageChange = (page, perPage = pagination.per_page) => {
    setPagination((prev) => ({
      ...prev,
      current_page: page,
      per_page: perPage,
    }));
  };

  // table data
  const formatTableData = (data) => {
    if (!Array.isArray(data)) return [];

    return data.map((item) => {
      const mainQuestions = item?.reflection?.main_questions || [];
      const titleQuestion = mainQuestions.find((q) => q?.keywords === "title");
      let timestamp = "";
      if (item?.is_complete) {
        timestamp = item.completed_at;
      } else {
        timestamp = item.created_at;
      }

      return {
        id: item.id,
        firstMainQuestionAnswer: titleQuestion?.answer?.text ?? "Title",
        name: titleQuestion?.question || "N/A",
        type: item?.reflection?.reflection_type ?? "N/A",
        is_completed: item?.is_complete,
        date: timestamp
          ? new Date(timestamp).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "N/A",
        rawData: item,
      };
    });
  };

  // Add this function to handle sorting
  const handleSort = (sortField) => {
    setFilters((prev) => {
      const newOrder =
        prev.sort_by === sortField
          ? prev.sort_order === "asc"
            ? "desc"
            : "asc"
          : "asc";

      return {
        ...prev,
        sort_by: sortField,
        sort_order: newOrder,
      };
    });
  };

  // delete reflection
  const handleDeleteReflection = async (reflectionId) => {
    try {
      await axios.delete(`${API_BASE}/user-reflections/${reflectionId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      fetchReflections();

      // Invalidate journal status queries for both AM and PM reflection types
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "journalStatus", "am"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "journalStatus", "pm"],
      });

      toast.success("Reflection Deleted successfully!");
    } catch (err) {
      toast.error("Error deleting reflection!");
      setError(err.response?.data?.message || "Failed to delete reflection");
    }
  };

  //see reflection modal
  const fetchReflectionDetails = async (reflectionId) => {
    try {
      const response = await axios.get(
        `${API_BASE}/user-reflections/${reflectionId}/details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const reflectionData = response.data.data.user_reflection;
      const transformedData = transformUserReflectionForModal(reflectionData);

      setReflectionDetails(transformedData);
      setSelectedReflectionDetails([]);
      setShowReflectionModal(true);
    } catch (err) {
      console.error("Error fetching reflection details:", err);
      setError(
        err.response?.data?.message || "Failed to fetch reflection details",
      );
    }
  };

  // Modify your row click handler
  const handleRowClick = (reflection) => {
    if (selectedIds.length > 0) return;
    fetchReflectionDetails(reflection.id);
  };

  // modal close for see reflection
  const modalRef1 = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showReflectionModal &&
        modalRef1.current &&
        !modalRef1.current.contains(event.target)
      ) {
        setShowReflectionModal(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showReflectionModal]);

  const [selectedReflectionDetails, setSelectedReflectionDetails] = useState(
    [],
  );
  const [currentReflectionIndex, setCurrentReflectionIndex] = useState(0);

  //  multiple reflections
  const fetchMultipleReflectionDetails = async (reflectionIds) => {
    try {
      const response = await axios.post(
        `${API_BASE}/user-reflections/details`,
        {
          ids: reflectionIds,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const list = response.data.data.user_reflections ?? [];
      setSelectedReflectionDetails(list.map(transformUserReflectionForModal));
      setReflectionDetails(null);
      setCurrentReflectionIndex(0);
      setShowReflectionModal(true);
    } catch (err) {
      console.error("Error fetching reflection details:", err);
      setError(
        err.response?.data?.message || "Failed to fetch reflection details",
      );
    }
  };

  // modal actions // delete, view, share and download
  const handleReflectionAction = async (action) => {
    if (action === "delete" && selectedIds.length > 0) {
      try {
        const response = await axios.delete(`${API_BASE}/user-reflections`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            ids: selectedIds,
          },
        });

        if (response.status === 200) {
          toast.success("Reflection Deleted successfully!");
          fetchReflections();

          // Invalidate journal status queries for both AM and PM reflection types
          queryClient.invalidateQueries({
            queryKey: ["dashboard", "journalStatus", "am"],
          });
          queryClient.invalidateQueries({
            queryKey: ["dashboard", "journalStatus", "pm"],
          });

          setSelectedIds([]);
          toggleModal();
        } else {
          console.error("Failed to delete reflections");
        }
      } catch (error) {
        console.error("Error deleting reflections", error);
      }
    } else if (action === "view" && selectedIds.length > 0) {
      fetchMultipleReflectionDetails(selectedIds);
    }

    setIsModalVisible(false);
  };

  //modal delete pop up
  const handleRequestDelete = (id) => {
    setReflectionToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (reflectionToDelete !== null) {
      await handleDeleteReflection(reflectionToDelete);
      setReflectionToDelete(null);
      setShowDeleteModal(false);
    }
  };

  const handleCancelDelete = () => {
    setReflectionToDelete(null);
    setShowDeleteModal(false);
  };

  return (
    <Layout>
      <div className="page-content">
        <div className="journal-content journal-chat-content">
          <div
            className="d-flex align-items-center"
            style={{ flexDirection: "row", width: "max-content" }}
          >
            <img
              src="/icons/back-arrow.svg"
              alt=""
              style={{
                marginRight: "9px",
                marginBottom: "32px",
                cursor: "pointer",
              }}
              onClick={() => navigate("/dashboard")}
            />
            <h1 className="pages-header text-heading-h2">Reflection Library</h1>
          </div>

          {/* {error && <div className="alert alert-danger">{error}</div>} */}

          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-3">
              <select
                className="text-body-b2 select-for-journal"
                name="reflection_type"
                value={filters.reflection_type}
                onChange={handleFilterChange}
              >
                <option value="">Select Reflection Types</option>
                {reflectionTypes.map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </select>

              <input
                type="text"
                className="header-search text-body-b2 input-for-journal"
                placeholder="Search by question..."
                name="main_question"
                value={filters.main_question}
                onChange={handleFilterChange}
              />
            </div>

            <div className="position-relative d-flex align-items-center">
              <img
                src="/icons/journal-linear-more.svg"
                alt="More"
                id="openModalIcon"
                style={{ cursor: "pointer", width: "24px" }}
                onClick={() => toggleModal()}
              />
              {isModalVisible && (
                <div
                  className="jurnal-library-more-option show"
                  ref={modalRef}
                  style={{ display: "block" }}
                >
                  <div className="different-options-available">
                    <div
                      className="each-option-available"
                      onClick={() => handleReflectionAction("share")}
                    >
                      <img src="/icons/share-image-jurnal.svg" alt="Share" />
                      <h4
                        className="text-body-b2 mb-0"
                        style={{ color: "#ffffff" }}
                      >
                        Share
                      </h4>
                    </div>
                    <div
                      className="each-option-available"
                      onClick={() => handleReflectionAction("download")}
                    >
                      <img
                        src="/icons/download-image-journal.svg"
                        alt="Download"
                      />
                      <h4
                        className="text-body-b2 mb-0"
                        style={{ color: "#ffffff" }}
                      >
                        Download
                      </h4>
                    </div>
                    <div
                      className="each-option-available"
                      onClick={() => handleReflectionAction("view")}
                    >
                      <img
                        src="/icons/journal-image-journal.svg"
                        alt="See Reflection"
                      />
                      <h4
                        className="text-body-b2 mb-0"
                        style={{ color: "#ffffff", cursor: "pointer" }}
                      >
                        See Reflection({selectedIds.length})
                      </h4>
                    </div>
                    <div
                      className="each-option-available"
                      onClick={() => handleReflectionAction("delete")}
                    >
                      <img src="/icons/journal-image-delete.svg" alt="Delete" />
                      <h4
                        className="text-body-b2 mb-0"
                        style={{ color: "#ffffff", cursor: "pointer" }}
                      >
                        Delete({selectedIds.length})
                      </h4>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="loader-container">
                <div className="loader"></div>
              </div>
            </div>
          ) : (
            <JournalTable
              data={formatTableData(reflections)}
              onRowClick={handleRowClick}
              pagination={pagination}
              onPageChange={handlePageChange}
              onSort={handleSort}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              onDeleteRequest={handleRequestDelete}
              setIsDownloadSelected={setIsDownloadSelected}
              isReflectionLibrary={true}
            />
          )}
        </div>
      </div>

      {/* modal for show reflection- bulk show */}
      {showReflectionModal && selectedReflectionDetails.length > 0 && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content memory-modal"
              ref={modalRef1}
              style={{ width: "687px" }}
            >
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowReflectionModal(false);
                }}
                aria-label="Close"
              >
                ×
              </button>
              <div
                className="d-flex justify-content-between align-items-center p-3"
                style={{ marginTop: "3rem", paddingTop: "3rem" }}
              >
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentReflectionIndex === 0}
                  onClick={() => setCurrentReflectionIndex((prev) => prev - 1)}
                >
                  Previous
                </button>

                <span>
                  Reflection {currentReflectionIndex + 1} of{" "}
                  {selectedReflectionDetails.length}
                </span>

                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={
                    currentReflectionIndex ===
                    selectedReflectionDetails.length - 1
                  }
                  onClick={() => setCurrentReflectionIndex((prev) => prev + 1)}
                >
                  Next
                </button>
              </div>

              <div className="memory-modal-header">
                <div className="justify-content-center">
                  <h5 className="memory-modal-title">Reflection Summary</h5>
                  <p className="memory-modal-title-second">
                    {
                      selectedReflectionDetails[currentReflectionIndex]
                        .reflection.reflection_type
                    }
                  </p>
                </div>
              </div>

              <div className="memory-modal-subtitle">
                {" "}
                <div className="meta-info">
                  {/* <img src="/assets/icons/discover.svg" alt="" />
                  <span>{journalDetails.journal.emotion_name}</span> */}
                </div>
                <div className="meta-info">
                  <span>
                    {new Date(
                      selectedReflectionDetails[currentReflectionIndex]
                        .completed_at ||
                        selectedReflectionDetails[currentReflectionIndex]
                          .created_at,
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                  <img src="/assets/icons/rotate-right.svg" alt="" />
                </div>
              </div>

              <div className="memory-modal-body">
                <div className="all-modal-content">
                  <div className="story-section">
                    {(
                      selectedReflectionDetails[currentReflectionIndex]
                        .reflection_answers ?? []
                    ).map((answer, index) => (
                      <div
                        key={index}
                        className="mt-4"
                        style={{ maxWidth: "100%" }}
                      >
                        <p
                          className="questions-title"
                          style={{
                            wordWrap: "break-word",
                            whiteSpace: "pre-wrap",
                            overflow: "hidden",
                          }}
                        >
                          {answer.main_question?.question ||
                            answer.follow_up_question?.question}
                        </p>
                        {/* If this question (main or follow-up) has keyword win-stack, render the DoneAchievedCard right below the question */}
                        {(() => {
                          const kw = (
                            answer.main_question?.keywords ||
                            answer.follow_up_question?.keywords ||
                            ""
                          )
                            .toString()
                            .toLowerCase()
                            .replace(/\s|-/g, "");
                          if (kw && kw.includes("winstack")) {
                            const row =
                              selectedReflectionDetails[currentReflectionIndex];
                            const dateStr = new Date(
                              row.completed_at || row.created_at,
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            });
                            return <DoneAchievedCard date={dateStr} />;
                          }
                          return null;
                        })()}
                        {answer.text && (
                          <p
                            className="ans-title"
                            style={{
                              wordWrap: "break-word",
                              whiteSpace: "pre-wrap",
                              overflow: "hidden",
                            }}
                            dangerouslySetInnerHTML={{ __html: answer.text }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="d-flex flex-column justify-content-between align-items-center gap-3">
                  <button
                    className="btn-2 btn-1-primary"
                    style={{ maxWidth: "205px", width: "100%" }}
                    onClick={handleGenerateAndOpenModal}
                  >
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* single reflection details show */}
      {showReflectionModal && reflectionDetails && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ width: "auto", maxWidth: "none " }}
          >
            <div
              className="modal-content memory-modal"
              ref={modalRef1}
              style={{ width: "687px" }}
            >
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowReflectionModal(false);
                  setIsDownloadSelected(false);
                }}
                aria-label="Close"
              >
                ×
              </button>

              <div className="memory-modal-header">
                <div className="justify-content-center">
                  <h5 className="memory-modal-title">Reflection Summary</h5>
                  <p className="memory-modal-title-second">
                    {reflectionDetails?.reflection?.reflection_type ||
                      "Reflection"}
                  </p>
                </div>
              </div>

              <div className="memory-modal-subtitle">
                <div className="meta-info"></div>
                <div className="meta-info">
                  <span>
                    {new Date(
                      reflectionDetails?.completed_at ||
                        reflectionDetails?.created_at,
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                  <img src="/assets/icons/rotate-right.svg" alt="" />
                </div>
              </div>

              <div className="memory-modal-body">
                <div className="all-modal-content">
                  <div className="story-section">
                    {reflectionDetails.reflection_answers?.length > 0 ? (
                      reflectionDetails.reflection_answers.map(
                        (answer, index) => (
                          <div key={index} className="mt-4">
                            <p
                              className="questions-title"
                              style={{
                                wordWrap: "break-word",
                                whiteSpace: "pre-wrap",
                                overflow: "hidden",
                              }}
                            >
                              {answer.main_question?.question ||
                                answer.follow_up_question?.question ||
                                "Question not available"}
                            </p>
                            {/* Insert DoneAchievedCard when the question (main or follow-up) has win-stack keyword */}
                            {(() => {
                              const kw = (
                                answer.main_question?.keywords ||
                                answer.follow_up_question?.keywords ||
                                ""
                              )
                                .toString()
                                .toLowerCase()
                                .replace(/\s|-/g, "");
                              if (kw && kw.includes("winstack")) {
                                const dateStr = new Date(
                                  reflectionDetails?.completed_at ||
                                    reflectionDetails?.created_at,
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                });
                                return <DoneAchievedCard date={dateStr} />;
                              }
                              return null;
                            })()}
                            {answer.text && (
                              <p
                                className="ans-title"
                                style={{
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                  overflow: "hidden",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: answer.text,
                                }}
                              />
                            )}
                          </div>
                        ),
                      )
                    ) : (
                      <p>No reflection questions found for this entry</p>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-column justify-content-between align-items-center gap-3">
                  {isDownloadSelected ? (
                    <button
                      className="btn-2 btn-1-primary"
                      style={{ maxWidth: "205px", width: "100%" }}
                      onClick={handleDownloadPDF}
                    >
                      Download
                    </button>
                  ) : (
                    <button
                      className="btn-2 btn-1-primary"
                      style={{ maxWidth: "205px", width: "100%" }}
                      onClick={handleGenerateAndOpenModal}
                    >
                      Share
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* delete  modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          aria-labelledby="notCompletedJournalModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{ backgroundColor: "#132534" }}
            >
              <div className="modal-header position-relative d-flex justify-content-center">
                <button
                  type="button"
                  className="btn-close btn-close-white position-absolute"
                  style={{
                    top: "30px",
                    right: "47px",
                    color: "#e6ecf1",
                  }}
                  onClick={handleCancelDelete}
                ></button>
                <h1
                  className="modal-title"
                  style={{ color: "#e6ecf1", fontSize: "24px" }}
                >
                  Delete Reflection
                </h1>
              </div>
              <div className="modal-body">
                <p
                  className="text-body-b2 mb-4 text-center"
                  style={{ color: "#e6ecf1", fontSize: "18px" }}
                >
                  Are you sure you want to delete the reflection?
                </p>

                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", background: "#db4545" }}
                    onClick={handleConfirmDelete}
                  >
                    Yes
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px" }}
                    onClick={handleCancelDelete}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <>
          <ShareDocumentModal
            show={isModalOpen}
            title={titleToShare}
            file={fileToShare}
            content={contentToShare}
            onClose={handleCloseModal}
          />
          <div className="modal-backdrop show"></div>
        </>
      )}
    </Layout>
  );
}
